import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ActionService } from '@ignitesales/neuro-application/src/neuro-guide/services/ActionService.mjs';
import ActionModal from './ActionModal';
import ActionSlidePanel from './ActionSlidePanel';

/**
 * Central owner of all action-driven modals (pop) and slide panels.
 *
 * Before this, four components each kept their own modal state and (after
 * DIALOGUE-2575) their own `ignite:closeModal` listener: DialoguePageContainer,
 * ActionButton, useActionTrigger and ContactShareButtons. This provider owns a
 * single stack and is the single listener for `ignite:closeModal`, so callers
 * just `openModal(action, context)` and never wire a listener again (DIALOGUE-2576).
 */
const ModalContext = createContext(null);

let _seq = 0;
const nextId = () => `modal-${++_seq}`;

// Respect fireOnce: mark the action fired on close, but only when the entry carries
// a trigger (DPC / useActionTrigger do; ActionButton / ContactShareButtons historically
// did not gate on fireOnce and pass no trigger — so they stay no-op). Guarded so a
// tracking failure can never block dismissal. MUST be called OUTSIDE the state updater
// (updaters must stay pure and may run more than once).
const markFired = (entry) => {
    if (!entry?.action || !entry.context?.trigger) return;
    try {
        ActionService.markActionAsFired(entry.action, entry.context.trigger);
    } catch (error) {
        console.error('ModalProvider: failed to mark action as fired:', error);
    }
};

export const ModalProvider = ({ children }) => {
    // [{ id, kind: 'pop' | 'slide', action, context }], newest on top.
    //
    // `stackRef` is the synchronous source of truth and `stack` is its rendered mirror.
    // Every write goes through `commit`, and every close helper reads the ref, not the
    // React state. This is what makes two `ignite:closeModal` events in one tick (neuro
    // clickActionUtils dispatches synchronously per configured click action) each see
    // the other's result: no double-pop, no entry left unmarked. Reading `stack` from a
    // useCallback closure instead would race the functional setState (the TOCTOU found
    // in review). It also makes every helper identity-stable, so consumers of
    // `useModal()` don't re-render on every open/close elsewhere in the page.
    const [stack, setStack] = useState([]);
    const stackRef = useRef(stack);
    const commit = useCallback((next) => {
        stackRef.current = next;
        setStack(next);
    }, []);

    const openModal = useCallback((action, context = {}) => {
        if (!action) return null;
        const kind = action.actionType === 'slide' ? 'slide' : 'pop';
        const id = nextId();
        commit([...stackRef.current, { id, kind, action, context }]);
        return id;
    }, [commit]);

    // Close helpers take the closing entry off the ref, commit the new stack, then mark
    // it fired — AFTER the state write, so a tracking failure never blocks dismissal.
    const closeById = useCallback((id) => {
        const current = stackRef.current;
        const entry = current.find(m => m.id === id);
        if (!entry) return;
        commit(current.filter(m => m.id !== id));
        markFired(entry);
    }, [commit]);

    const closeTop = useCallback(() => {
        const current = stackRef.current;
        if (current.length === 0) return;
        commit(current.slice(0, -1));
        markFired(current[current.length - 1]);
    }, [commit]);

    const closeAll = useCallback(() => {
        const closing = stackRef.current;
        if (closing.length === 0) return;
        commit([]);
        closing.forEach(markFired);
    }, [commit]);

    // Clear all modals WITHOUT marking them fired — used on page navigation so a modal
    // merely dismissed by leaving the page isn't recorded as completed (preserves the
    // pre-2576 page-change reset; avoids tripping fireOnce / completion analytics).
    const clearAll = useCallback(() => {
        commit([]);
    }, [commit]);

    // The in-layout `closeModal` widget action dispatches this global event
    // (neuro clickActionUtils). One listener here replaces the per-component ones.
    // It closes ALL open modals, not just the top one: that is what DialoguePageContainer
    // did before 2576 and what DIALOGUE-2575 does in the other three owners, so a
    // `closeModal` button in a lower modal of a stack still dismisses the whole stack.
    useEffect(() => {
        const handleCloseModal = () => closeAll();
        window.addEventListener('ignite:closeModal', handleCloseModal);
        return () => window.removeEventListener('ignite:closeModal', handleCloseModal);
    }, [closeAll]);

    const value = useMemo(
        () => ({ openModal, closeTop, closeById, closeAll, clearAll }),
        [openModal, closeTop, closeById, closeAll, clearAll]
    );

    return (
        <ModalContext.Provider value={value}>
            {children}
            {stack.map(({ id, kind, action, context }) => (
                kind === 'slide'
                    ? <ActionSlidePanel key={id} action={action} isOpen={true} onClose={() => closeById(id)} context={context} />
                    : <ActionModal key={id} action={action} isOpen={true} onClose={() => closeById(id)} context={context} />
            ))}
        </ModalContext.Provider>
    );
};

export const useModal = () => {
    const ctx = useContext(ModalContext);
    if (!ctx) {
        throw new Error('useModal must be used within a ModalProvider');
    }
    return ctx;
};

export default ModalProvider;
